import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  previewCsv,
  checkCsvDuplicates,
  executeCsvImport,
  getStatuses,
  getCampaigns,
  getSources,
  getSellers,
  createCampaign,
  createSource,
} from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  RadioGroup,
  RadioGroupItem,
} from "../components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Users,
  Phone,
  Mail,
  Plus,
  Megaphone,
  Package,
  Shuffle,
  UserPlus,
} from "lucide-react";
import { cn } from "../lib/utils";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { getInitials } from "../lib/utils";

const CRM_FIELDS = [
  { value: "ignore", label: "Ignorer" },
  { value: "fullName", label: "Nom Prénom (combiné)" },
  { value: "firstName", label: "Prénom" },
  { value: "lastName", label: "Nom" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Téléphone" },
  { value: "birthDate", label: "Date de naissance" },
  { value: "address", label: "Adresse" },
  { value: "city", label: "Ville" },
  { value: "zip", label: "Code postal" },
  { value: "country", label: "Pays" },
  { value: "source", label: "Source" },
];

const DUPLICATE_ACTIONS = [
  {
    value: "skip",
    label: "Ignorer les doublons",
    description: "Les doublons ne seront pas importés",
  },
  {
    value: "import",
    label: "Importer quand même",
    description: "Créer les leads même si doublon téléphone",
  },
  {
    value: "update",
    label: "Mettre à jour l'existant",
    description: "Mettre à jour les leads existants avec les nouvelles données",
  },
];

export const ImportPage = () => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [defaultStatusId, setDefaultStatusId] = useState("");
  const [duplicateData, setDuplicateData] = useState(null);
  const [globalDuplicateAction, setGlobalDuplicateAction] = useState("skip");
  const [duplicateResolutions, setDuplicateResolutions] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Dialog states for creating new campaign/source
  const [showNewCampaignDialog, setShowNewCampaignDialog] = useState(false);
  const [showNewSourceDialog, setShowNewSourceDialog] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignColor, setNewCampaignColor] = useState("#8B5CF6");
  const [newSourceName, setNewSourceName] = useState("");
  const [creatingItem, setCreatingItem] = useState(false);

  // Seller assignment states for import
  const [assignmentMode, setAssignmentMode] = useState("none"); // "none", "select", "shuffle"
  const [selectedSellerIds, setSelectedSellerIds] = useState([]);
  const [shuffleOnImport, setShuffleOnImport] = useState(false);

  // Load campaigns, sources, statuses and sellers on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [campaignsRes, statusesRes, sourcesRes, sellersRes] = await Promise.all([
          getCampaigns(),
          getStatuses(),
          getSources(),
          getSellers(),
        ]);
        setCampaigns(campaignsRes.data.filter((c) => c.isActive));
        setStatuses(statusesRes.data);
        setSources(sourcesRes.data);
        setSellers(sellersRes.data.filter((s) => s.isActive !== false));

        const defaultStatus = statusesRes.data.find((s) => s.isDefault);
        if (defaultStatus) {
          setDefaultStatusId(defaultStatus.id);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".csv")) {
      toast.error("Seuls les fichiers CSV sont acceptés");
      return;
    }

    setFile(selectedFile);
    setLoading(true);

    try {
      const previewRes = await previewCsv(selectedFile);
      setPreview(previewRes.data);
      setMapping(previewRes.data.suggestedMapping || {});
      setStep(2);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erreur lors de la lecture du fichier"
      );
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (csvColumn, crmField) => {
    setMapping((prev) => ({
      ...prev,
      [csvColumn]: crmField,
    }));
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) {
      toast.error("Le nom de la campagne est requis");
      return;
    }
    setCreatingItem(true);
    try {
      const res = await createCampaign({ 
        name: newCampaignName, 
        color: newCampaignColor 
      });
      setCampaigns((prev) => [...prev, res.data]);
      setSelectedCampaignId(res.data.id);
      setShowNewCampaignDialog(false);
      setNewCampaignName("");
      toast.success("Campagne créée avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la campagne");
    } finally {
      setCreatingItem(false);
    }
  };

  const handleCreateSource = async () => {
    if (!newSourceName.trim()) {
      toast.error("Le nom de la source est requis");
      return;
    }
    setCreatingItem(true);
    try {
      const res = await createSource({ name: newSourceName });
      setSources((prev) => [...prev, res.data]);
      setSelectedSourceId(res.data.id);
      setShowNewSourceDialog(false);
      setNewSourceName("");
      toast.success("Source créée avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la source");
    } finally {
      setCreatingItem(false);
    }
  };

  const handleCheckDuplicates = async () => {
    if (!selectedCampaignId) {
      toast.error("Veuillez sélectionner une campagne");
      return;
    }

    setLoading(true);
    try {
      const res = await checkCsvDuplicates(file, mapping);
      setDuplicateData(res.data);

      // Initialize resolutions
      const resolutions = {};
      [...res.data.duplicatesInFile, ...res.data.duplicatesInDatabase].forEach(
        (dup) => {
          resolutions[dup.rowIndex] = globalDuplicateAction;
        }
      );
      setDuplicateResolutions(resolutions);

      if (
        res.data.duplicatesInFile.length > 0 ||
        res.data.duplicatesInDatabase.length > 0
      ) {
        setStep(4); // Go to duplicate resolution step
      } else {
        setStep(5); // Skip to final review
      }
    } catch (error) {
      toast.error("Erreur lors de la vérification des doublons");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedCampaignId) {
      toast.error("Veuillez sélectionner une campagne");
      return;
    }

    setLoading(true);
    try {
      // Prepare duplicate resolutions
      const resolutionsList = Object.entries(duplicateResolutions).map(
        ([rowIndex, action]) => ({
          rowIndex: parseInt(rowIndex),
          action,
        })
      );

      // Prepare seller assignment
      const sellersToAssign = assignmentMode !== "none" ? selectedSellerIds : null;
      const shouldShuffle = assignmentMode === "shuffle" || shuffleOnImport;

      const res = await executeCsvImport(
        file,
        mapping,
        selectedCampaignId,
        selectedSourceId || null,
        defaultStatusId,
        globalDuplicateAction,
        resolutionsList.length > 0 ? resolutionsList : null,
        sellersToAssign,
        shouldShuffle
      );
      setResult(res.data);
      setStep(6);
      toast.success(`Import terminé: ${res.data.imported} leads importés`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  // Toggle seller selection for import assignment
  const toggleSellerForImport = (sellerId) => {
    setSelectedSellerIds(prev => 
      prev.includes(sellerId) 
        ? prev.filter(id => id !== sellerId)
        : [...prev, sellerId]
    );
  };

  const getMappedFieldsCount = () => {
    return Object.values(mapping).filter((v) => v && v !== "ignore").length;
  };

  const applyGlobalAction = (action) => {
    setGlobalDuplicateAction(action);
    const newResolutions = { ...duplicateResolutions };
    Object.keys(newResolutions).forEach((key) => {
      newResolutions[key] = action;
    });
    setDuplicateResolutions(newResolutions);
  };

  const totalDuplicates = duplicateData
    ? duplicateData.duplicatesInFile.length +
      duplicateData.duplicatesInDatabase.length
    : 0;

  // Step 1: Upload
  const renderStep1 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="import-step-1">
      <CardHeader className="text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <FileSpreadsheet className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">Import CSV</CardTitle>
        <CardDescription>
          Importez vos leads depuis un fichier CSV
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors",
            loading && "opacity-50 pointer-events-none"
          )}
        >
          {loading ? (
            <Loader2 className="h-12 w-12 text-muted-foreground mx-auto animate-spin" />
          ) : (
            <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
          )}
          <p className="mt-4 font-medium">
            {loading
              ? "Analyse en cours..."
              : "Cliquez pour sélectionner un fichier"}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Formats acceptés: CSV (UTF-8, Latin-1)
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="csv-file-input"
        />
      </CardContent>
    </Card>
  );

  // Step 2: Mapping
  const renderStep2 = () => (
    <div className="space-y-6" data-testid="import-step-2">
      <Card>
        <CardHeader>
          <CardTitle>Configuration du mapping</CardTitle>
          <CardDescription>
            Associez les colonnes de votre fichier aux champs du CRM.
            {preview?.suggestedMapping &&
              Object.keys(preview.suggestedMapping).length > 0 && (
                <span className="text-green-600 ml-2">
                  ({Object.keys(preview.suggestedMapping).length} colonnes
                  détectées automatiquement)
                </span>
              )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {preview?.headers.map((header) => (
              <div key={header} className="flex items-center gap-4">
                <div className="w-1/3">
                  <Badge variant="outline" className="font-mono">
                    {header}
                  </Badge>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="w-1/3">
                  <Select
                    value={mapping[header] || "ignore"}
                    onValueChange={(v) => handleMappingChange(header, v)}
                  >
                    <SelectTrigger data-testid={`mapping-${header}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CRM_FIELDS.map((field) => (
                        <SelectItem key={field.value} value={field.value}>
                          {field.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-1/3 text-sm text-muted-foreground truncate">
                  {preview?.preview[0]?.[header] || "-"}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => {
            setStep(1);
            setFile(null);
            setPreview(null);
          }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <Button
          onClick={() => setStep(3)}
          disabled={getMappedFieldsCount() === 0}
        >
          Suivant
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Step 3: Campaign & Status Selection
  const renderStep3 = () => (
    <div className="space-y-6" data-testid="import-step-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Campagne *
            </CardTitle>
            <CardDescription>
              Associez les leads importés à une campagne
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedCampaignId}
              onValueChange={(v) => {
                if (v === "new") {
                  setShowNewCampaignDialog(true);
                } else {
                  setSelectedCampaignId(v);
                }
              }}
            >
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder="Sélectionner une campagne" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: campaign.color }}
                      />
                      {campaign.name}
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="new" className="text-primary font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Créer une nouvelle campagne
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Source Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Source / Fournisseur
            </CardTitle>
            <CardDescription>
              D'où proviennent ces leads ? (optionnel)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedSourceId}
              onValueChange={(v) => {
                if (v === "new") {
                  setShowNewSourceDialog(true);
                } else if (v === "none") {
                  setSelectedSourceId("");
                } else {
                  setSelectedSourceId(v);
                }
              }}
            >
              <SelectTrigger data-testid="source-select">
                <SelectValue placeholder="Sélectionner une source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">Aucune source</span>
                </SelectItem>
                {sources.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.name}
                  </SelectItem>
                ))}
                <SelectItem value="new" className="text-primary font-medium">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Créer une nouvelle source
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Statut par défaut</CardTitle>
            <CardDescription>
              Statut attribué aux nouveaux leads importés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={defaultStatusId} onValueChange={setDefaultStatusId}>
              <SelectTrigger data-testid="default-status-select">
                <SelectValue placeholder="Sélectionner un statut" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: status.color }}
                      />
                      {status.name}
                      {status.isDefault && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Défaut
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Preview Table */}
      <Card>
        <CardHeader>
          <CardTitle>Aperçu des données</CardTitle>
          <CardDescription>
            {preview?.rowCount} lignes détectées • {getMappedFieldsCount()}{" "}
            colonnes mappées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {preview?.headers
                    .filter((h) => mapping[h] && mapping[h] !== "ignore")
                    .map((header) => (
                      <TableHead key={header}>
                        <div className="space-y-1">
                          <Badge variant="outline" className="font-mono text-xs">
                            {header}
                          </Badge>
                          <p className="text-xs font-normal">
                            →{" "}
                            {
                              CRM_FIELDS.find((f) => f.value === mapping[header])
                                ?.label
                            }
                          </p>
                        </div>
                      </TableHead>
                    ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview?.preview.slice(0, 5).map((row, i) => (
                  <TableRow key={i}>
                    {preview?.headers
                      .filter((h) => mapping[h] && mapping[h] !== "ignore")
                      .map((header) => (
                        <TableCell
                          key={header}
                          className="font-mono text-sm"
                        >
                          {row[header] || "-"}
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <Button
          onClick={handleCheckDuplicates}
          disabled={loading || !selectedCampaignId}
          data-testid="check-duplicates-button"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Vérification...
            </>
          ) : (
            <>
              Vérifier les doublons
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Step 4: Duplicate Resolution
  const renderStep4 = () => (
    <div className="space-y-6" data-testid="import-step-4">
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <AlertTriangle className="h-5 w-5" />
            Doublons téléphone détectés
          </CardTitle>
          <CardDescription className="text-orange-600">
            {totalDuplicates} doublon(s) trouvé(s) sur {duplicateData?.totalRows}{" "}
            lignes
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Global Action */}
      <Card>
        <CardHeader>
          <CardTitle>Action globale pour tous les doublons</CardTitle>
          <CardDescription>
            Appliquer cette action à tous les doublons
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={globalDuplicateAction}
            onValueChange={applyGlobalAction}
            className="space-y-3"
          >
            {DUPLICATE_ACTIONS.map((action) => (
              <div
                key={action.value}
                className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50"
              >
                <RadioGroupItem value={action.value} id={action.value} />
                <div className="flex-1">
                  <Label htmlFor={action.value} className="font-medium">
                    {action.label}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {action.description}
                  </p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Duplicates in File */}
      {duplicateData?.duplicatesInFile.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-yellow-600" />
              Doublons dans le fichier ({duplicateData.duplicatesInFile.length})
            </CardTitle>
            <CardDescription>
              Ces numéros apparaissent plusieurs fois dans votre fichier CSV
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ligne</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicateData.duplicatesInFile.map((dup) => (
                    <TableRow key={dup.rowIndex}>
                      <TableCell className="font-mono">
                        #{dup.rowIndex + 2}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {dup.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        {dup.csvData[
                          Object.keys(mapping).find(
                            (k) => mapping[k] === "firstName"
                          )
                        ] || ""}{" "}
                        {dup.csvData[
                          Object.keys(mapping).find(
                            (k) => mapping[k] === "lastName"
                          )
                        ] || ""}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={
                            duplicateResolutions[dup.rowIndex] ||
                            globalDuplicateAction
                          }
                          onValueChange={(v) =>
                            setDuplicateResolutions((prev) => ({
                              ...prev,
                              [dup.rowIndex]: v,
                            }))
                          }
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">Ignorer</SelectItem>
                            <SelectItem value="import">Importer</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Duplicates in Database */}
      {duplicateData?.duplicatesInDatabase.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Doublons existants ({duplicateData.duplicatesInDatabase.length})
            </CardTitle>
            <CardDescription>
              Ces numéros existent déjà dans votre base de données
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ligne CSV</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Données CSV</TableHead>
                    <TableHead>Lead existant</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {duplicateData.duplicatesInDatabase.map((dup) => (
                    <TableRow key={dup.rowIndex}>
                      <TableCell className="font-mono">
                        #{dup.rowIndex + 2}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          {dup.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">
                            {dup.csvData[
                              Object.keys(mapping).find(
                                (k) => mapping[k] === "firstName"
                              )
                            ] || ""}{" "}
                            {dup.csvData[
                              Object.keys(mapping).find(
                                (k) => mapping[k] === "lastName"
                              )
                            ] || ""}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {dup.csvData[
                              Object.keys(mapping).find(
                                (k) => mapping[k] === "email"
                              )
                            ] || ""}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {dup.existingLead && (
                          <div className="text-sm">
                            <p className="font-medium">
                              {dup.existingLead.firstName}{" "}
                              {dup.existingLead.lastName}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {dup.existingLead.statusName && (
                                <Badge
                                  variant="outline"
                                  style={{
                                    borderColor: dup.existingLead.statusColor,
                                    color: dup.existingLead.statusColor,
                                  }}
                                >
                                  {dup.existingLead.statusName}
                                </Badge>
                              )}
                              {dup.existingLead.campaignName && (
                                <span className="text-xs text-muted-foreground">
                                  {dup.existingLead.campaignName}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={
                            duplicateResolutions[dup.rowIndex] ||
                            globalDuplicateAction
                          }
                          onValueChange={(v) =>
                            setDuplicateResolutions((prev) => ({
                              ...prev,
                              [dup.rowIndex]: v,
                            }))
                          }
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="skip">Ignorer</SelectItem>
                            <SelectItem value="import">Importer</SelectItem>
                            <SelectItem value="update">Mettre à jour</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(3)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <Button onClick={() => setStep(5)}>
          Continuer
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Step 5: Final Review
  const renderStep5 = () => {
    const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId);
    const selectedSource = sources.find((s) => s.id === selectedSourceId);
    const skippedCount = Object.values(duplicateResolutions).filter(
      (a) => a === "skip"
    ).length;
    const importCount = duplicateData
      ? duplicateData.cleanRows +
        Object.values(duplicateResolutions).filter((a) => a === "import").length
      : preview?.rowCount || 0;

    return (
      <div className="space-y-6" data-testid="import-step-5">
        <Card>
          <CardHeader>
            <CardTitle>Récapitulatif de l'import</CardTitle>
            <CardDescription>
              Vérifiez les informations avant de lancer l'import
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Fichier</p>
                <p className="font-medium">{file?.name}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Campagne</p>
                <div className="flex items-center gap-2">
                  {selectedCampaign && (
                    <>
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: selectedCampaign.color }}
                      />
                      <p className="font-medium">{selectedCampaign.name}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground">Source</p>
                <p className="font-medium">
                  {selectedSource ? selectedSource.name : <span className="text-muted-foreground italic">Aucune</span>}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-3xl font-bold text-green-600">
                  {importCount}
                </p>
                <p className="text-sm text-green-700">À importer</p>
              </div>
              <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                <p className="text-3xl font-bold text-yellow-600">
                  {skippedCount}
                </p>
                <p className="text-sm text-yellow-700">Doublons ignorés</p>
              </div>
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-3xl font-bold text-blue-600">
                  {Object.values(duplicateResolutions).filter(
                    (a) => a === "update"
                  ).length}
                </p>
                <p className="text-sm text-blue-700">À mettre à jour</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seller Assignment Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Attribution des leads
            </CardTitle>
            <CardDescription>
              Choisissez comment attribuer les leads importés aux vendeurs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup 
              value={assignmentMode} 
              onValueChange={(value) => {
                setAssignmentMode(value);
                if (value === "none") {
                  setSelectedSellerIds([]);
                  setShuffleOnImport(false);
                }
              }}
            >
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="none" id="assignment-none" />
                <Label htmlFor="assignment-none" className="flex-1 cursor-pointer">
                  <span className="font-medium">Non attribués</span>
                  <p className="text-sm text-muted-foreground">
                    Les leads seront importés sans attribution
                  </p>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="select" id="assignment-select" />
                <Label htmlFor="assignment-select" className="flex-1 cursor-pointer">
                  <span className="font-medium">Attribuer aux vendeurs sélectionnés</span>
                  <p className="text-sm text-muted-foreground">
                    Distribution équitable (round-robin) entre les vendeurs choisis
                  </p>
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50">
                <RadioGroupItem value="shuffle" id="assignment-shuffle" />
                <Label htmlFor="assignment-shuffle" className="flex-1 cursor-pointer">
                  <span className="font-medium flex items-center gap-2">
                    <Shuffle className="h-4 w-4" />
                    Mixer le partage
                  </span>
                  <p className="text-sm text-muted-foreground">
                    Distribution aléatoire entre les vendeurs choisis
                  </p>
                </Label>
              </div>
            </RadioGroup>

            {(assignmentMode === "select" || assignmentMode === "shuffle") && (
              <div className="space-y-3 pt-2">
                <p className="text-sm font-medium">
                  Sélectionner les vendeurs ({selectedSellerIds.length} sélectionné{selectedSellerIds.length !== 1 ? "s" : ""})
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sellers.map((seller) => (
                    <div
                      key={seller.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        selectedSellerIds.includes(seller.id)
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                      onClick={() => toggleSellerForImport(seller.id)}
                    >
                      <Checkbox
                        checked={selectedSellerIds.includes(seller.id)}
                        onCheckedChange={() => toggleSellerForImport(seller.id)}
                      />
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          {getInitials(seller.name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">{seller.name}</span>
                    </div>
                  ))}
                </div>
                {sellers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun vendeur actif disponible
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(totalDuplicates > 0 ? 4 : 3)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <Button
            onClick={handleImport}
            disabled={loading || ((assignmentMode === "select" || assignmentMode === "shuffle") && selectedSellerIds.length === 0)}
            data-testid="execute-import-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Import en cours...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Lancer l'import
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Step 6: Result
  const renderStep6 = () => (
    <Card className="max-w-2xl mx-auto" data-testid="import-step-6">
      <CardHeader className="text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <CardTitle className="text-2xl">Import terminé !</CardTitle>
        <CardDescription>Voici le résumé de votre import</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-2xl font-bold text-green-600">
              {result?.imported || 0}
            </p>
            <p className="text-xs text-green-700">Importés</p>
          </div>
          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
            <p className="text-2xl font-bold text-yellow-600">
              {result?.duplicatesSkipped || 0}
            </p>
            <p className="text-xs text-yellow-700">Ignorés</p>
          </div>
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-2xl font-bold text-blue-600">
              {result?.duplicatesUpdated || 0}
            </p>
            <p className="text-xs text-blue-700">Mis à jour</p>
          </div>
          <div className="p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-2xl font-bold text-red-600">
              {result?.errors || 0}
            </p>
            <p className="text-xs text-red-700">Erreurs</p>
          </div>
        </div>

        {result?.duplicatesImported > 0 && (
          <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
            <p className="text-sm text-orange-700">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              {result.duplicatesImported} doublon(s) importé(s) malgré
              l'existence de leads similaires
            </p>
          </div>
        )}

        {result?.errorDetails?.length > 0 && (
          <div className="space-y-2">
            <p className="font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Détails des erreurs
            </p>
            <div className="max-h-40 overflow-y-auto bg-muted/50 rounded-lg p-3 text-sm font-mono">
              {result.errorDetails.map((error, i) => (
                <p key={i} className="text-red-600">
                  {error}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/leads")}
          >
            Voir les leads
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              setStep(1);
              setFile(null);
              setPreview(null);
              setMapping({});
              setResult(null);
              setDuplicateData(null);
              setDuplicateResolutions({});
            }}
          >
            Nouvel import
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const totalSteps = 6;
  const currentProgress = (step / totalSteps) * 100;

  return (
    <div className="space-y-6" data-testid="import-page">
      {/* Progress */}
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          {["Upload", "Mapping", "Campagne", "Doublons", "Validation", "Résultat"].map(
            (label, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-2",
                  step > i + 1 && "text-green-600",
                  step === i + 1 && "text-primary font-medium"
                )}
              >
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm",
                    step > i + 1 && "bg-green-100 text-green-600",
                    step === i + 1 && "bg-primary text-primary-foreground",
                    step < i + 1 && "bg-muted text-muted-foreground"
                  )}
                >
                  {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className="hidden sm:inline text-xs">{label}</span>
              </div>
            )
          )}
        </div>
        <Progress value={currentProgress} className="h-2" />
      </div>

      {/* Steps */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
      {step === 5 && renderStep5()}
      {step === 6 && renderStep6()}

      {/* New Campaign Dialog */}
      <Dialog open={showNewCampaignDialog} onOpenChange={setShowNewCampaignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Créer une nouvelle campagne
            </DialogTitle>
            <DialogDescription>
              La campagne sera créée et automatiquement sélectionnée pour cet import.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Nom de la campagne *</Label>
              <Input
                id="campaign-name"
                placeholder="Ex: Campagne Janvier 2025"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                data-testid="new-campaign-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-color">Couleur</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="campaign-color"
                  value={newCampaignColor}
                  onChange={(e) => setNewCampaignColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <span className="text-sm text-muted-foreground">{newCampaignColor}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCampaignDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateCampaign} disabled={creatingItem}>
              {creatingItem ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                "Créer la campagne"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Source Dialog */}
      <Dialog open={showNewSourceDialog} onOpenChange={setShowNewSourceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Créer une nouvelle source
            </DialogTitle>
            <DialogDescription>
              La source sera créée et automatiquement sélectionnée pour cet import.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="source-name">Nom de la source / fournisseur *</Label>
              <Input
                id="source-name"
                placeholder="Ex: Google Ads, Facebook, Fournisseur X"
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                data-testid="new-source-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewSourceDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateSource} disabled={creatingItem}>
              {creatingItem ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                "Créer la source"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
